import React from "react";

import { css } from "@emotion/css";
import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { AlertType } from "@/models/AlertModel.ts";
import { AlertSettingsTable } from "@/pages/patient-settings/alert-settings/AlertSettingsTable.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";
import { Tab } from "@/uiKit/atoms/Tab.tsx";
import { TabList } from "@/uiKit/atoms/TabList.tsx";

import { AlertCreationDialog } from "./AlertCreationDialog";

type AlertTabsSelectorProps = {
  patientId: string;
  tab: AlertType;
};

const AlertTabsSelector = ({ tab, patientId }: AlertTabsSelectorProps) => {
  const { t } = useTranslation();
  const alertSettingsQuery = Queries.alerts.useAlertConfigList(patientId);

  return (
    <TabList id="patient-alerts-settings" selectedTab={tab}>
      <Tab
        id="patient-alerts-settings-cgm"
        label={t("alertConfig.cgm")}
        value="cgm"
        selected={tab === "cgm"}
        link={{
          to: ".",
          search: {
            alert: "cgm",
          },
        }}
        count={alertSettingsQuery.data?.filter(item => item.type === "cgm").length || 0}
      />
      <Tab
        id="patient-alerts-settings-bgm"
        label={t("alertConfig.bgm")}
        value="bgm"
        selected={tab === "bgm"}
        link={{
          to: ".",
          search: {
            alert: "bgm",
          },
        }}
        count={alertSettingsQuery.data?.filter(item => item.type === "bgm").length || 0}
      />
      <Tab
        id="patient-alerts-settings-other"
        label={t("alertConfig.other")}
        value="other"
        selected={tab === "other"}
        link={{
          to: ".",
          search: {
            alert: "other",
          },
        }}
        count={alertSettingsQuery.data?.filter(item => item.type === "other").length || 0}
      />
    </TabList>
  );
};

export const AlertSettings: React.FC = () => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const patientId = usePatientIdFromURL();
  const alertSettingsQuery = Queries.alerts.useAlertConfigList(patientId);
  const displayMode = Queries.practitioner.useDiabetesDisplayMode(patientId);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  const tab =
    useSearch({
      from: "/_practitioner/patients/$patientId/settings",
    }).alert ||
    displayMode?.glycemiaIndicatorsDisplayMode ||
    "cgm";

  return (
    <Card className={styles.container}>
      <CardHeader title={t("alertConfig.title")} />
      <CardContent>
        <AlertTabsSelector tab={tab} patientId={patientId} />
        {alertSettingsQuery.isSuccess ? (
          <AlertSettingsTable alertType={tab} patientId={patientId} alertConfigList={alertSettingsQuery.data} />
        ) : (
          <Loader size="L" />
        )}
      </CardContent>
      <CardFooter>
        <Button
          id="create-alert-config"
          data-action="patient-settings.alert.navigate-create"
          variant="contained"
          startIcon={<Icons.plus className={styles.icon} />}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          {t("alertConfig.create")}
        </Button>
      </CardFooter>
      <Dialog fitContent open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)}>
        <AlertCreationDialog patientId={patientId} onClose={() => setIsCreateDialogOpen(false)} alertType={tab} />
      </Dialog>
    </Card>
  );
};

const makeStyles = () => ({
  container: css`
    flex: 1 1 0;
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
